/**
 * 从历史 frontend_metadata 还原 selectedKnowledge（编辑 / 面板勾选回显）
 */
import type { IKbExpandToken } from '../../utils/inlineMentionLabel';
import {
  mentionLabelMatchVariants,
  withKnowledgeMentionTrigger,
} from '../../utils/inlineMentionLabel';
import type { IInlineMentionRange } from '../../utils/inlineMentionRanges';
import { MENTION_BOUNDARY_MARKER } from '../../utils/inlineMentionRanges';
import type {
  IKnowledgeBaseItem,
  IKnowledgeDocItem,
} from '../hooks/useKnowledgeBaseApi';
import {
  getKnowledgeTagKey,
  type ISelectedKnowledgeTagData,
} from '../components/SelectedKnowledgeTag';
import { resolveTenantId } from './knowledgeBaseUtils';

function stripKbTrigger(text: string): string {
  return text.replace(/^@\s*/, '').trim();
}

function findKbForBody(
  kbList: IKnowledgeBaseItem[],
  body: string,
): IKnowledgeBaseItem | undefined {
  const trimmed = body.trim();
  if (!trimmed) return undefined;
  const sorted = [...kbList].sort(
    (a, b) => b.kbName.length - a.kbName.length,
  );
  return sorted.find(
    (kb) =>
      trimmed === kb.kbName
      || trimmed.startsWith(`${kb.kbName}-`)
      || trimmed.startsWith(`${kb.kbName} `),
  );
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function resolveDocNamesFromExpandToken(
  body: string,
  kb: IKnowledgeBaseItem,
  token: IKbExpandToken,
): string[] | null {
  const trimmedBody = body.trim();
  const countRe = new RegExp(`^${escapeRegExp(kb.kbName)}\\s+\\d+`);
  const docPrefix = `${kb.kbName}-`;

  if (!countRe.test(trimmedBody) && !trimmedBody.startsWith(docPrefix)) {
    return null;
  }

  const expandPrefix = `@${kb.kbName}-`;
  const idx = token.to.indexOf(expandPrefix);
  if (idx < 0) return null;

  const rest = token.to.slice(idx + expandPrefix.length);
  const markerIdx = rest.indexOf(MENTION_BOUNDARY_MARKER);
  const nextMentionIdx = rest.search(/\s@/);
  let end = rest.length;
  if (markerIdx >= 0) end = Math.min(end, markerIdx);
  if (nextMentionIdx >= 0) end = Math.min(end, nextMentionIdx);

  const docPart = rest.slice(0, end).trim();
  if (!docPart) return null;

  return docPart
    .split('、')
    .map((name) => name.trim())
    .filter(Boolean);
}

function makeTagItem(
  kb: IKnowledgeBaseItem,
  doc?: IKnowledgeDocItem,
  docNameFallback?: string,
): ISelectedKnowledgeTagData {
  if (doc) {
    return {
      kbId: kb.kbId,
      kbName: kb.kbName,
      tenantId: resolveTenantId(doc.tenantId, kb.tenantId),
      docId: doc.docId,
      docName: doc.docName,
      fileName: doc.fileName,
      fileType: doc.fileType,
    };
  }
  if (docNameFallback) {
    const matched = (kb.docs || []).find((item) => item.docName === docNameFallback);
    if (matched) {
      return makeTagItem(kb, matched);
    }
    return {
      kbId: kb.kbId,
      kbName: kb.kbName,
      tenantId: kb.tenantId,
      docName: docNameFallback,
    };
  }
  return {
    kbId: kb.kbId,
    kbName: kb.kbName,
    tenantId: kb.tenantId,
  };
}

/** 按顺序还原文档名，同名文档依次匹配未用过的 docId（支持 expand 里重复的 docName） */
function makeTagItemsFromDocNames(
  kb: IKnowledgeBaseItem,
  docNames: string[],
): ISelectedKnowledgeTagData[] {
  const pool = [...(kb.docs || [])];
  const usedDocIds = new Set<string>();

  return docNames
    .map((name) => {
      const trimmed = name.trim();
      if (!trimmed) return null;
      const doc = pool.find(
        (item) => item.docName === trimmed && !usedDocIds.has(item.docId),
      );
      if (doc) {
        usedDocIds.add(doc.docId);
        return makeTagItem(kb, doc);
      }
      return makeTagItem(kb, undefined, trimmed);
    })
    .filter((item): item is ISelectedKnowledgeTagData => Boolean(item?.docName));
}

function resolveDocsFromBody(
  body: string,
  kb: IKnowledgeBaseItem,
  kbDocUnit: string,
  kbExpandToken?: IKbExpandToken | null,
): ISelectedKnowledgeTagData[] {
  const trimmedBody = body.trim();
  const docs = kb.docs || [];

  const countRe = new RegExp(`^(.+?)\\s+(\\d+)${kbDocUnit}$`);
  const countMatch = trimmedBody.match(countRe);
  if (countMatch && countMatch[1].trim() === kb.kbName) {
    const count = Number(countMatch[2]);
    const expandedNames = kbExpandToken
      ? resolveDocNamesFromExpandToken(trimmedBody, kb, kbExpandToken)
      : null;
    if (expandedNames?.length) {
      return makeTagItemsFromDocNames(kb, expandedNames);
    }
    if (docs.length === count) {
      return docs.map((doc) => makeTagItem(kb, doc));
    }
    return docs.slice(0, count).map((doc) => makeTagItem(kb, doc));
  }

  const docPrefix = `${kb.kbName}-`;
  if (trimmedBody.startsWith(docPrefix)) {
    const docNames = trimmedBody
      .slice(docPrefix.length)
      .split('、')
      .map((name) => name.trim())
      .filter(Boolean);
    return makeTagItemsFromDocNames(kb, docNames);
  }

  if (trimmedBody === kb.kbName) {
    return [makeTagItem(kb)];
  }

  return [];
}

/** 编辑还原：用 KB 列表已知 label 定位 mention（允许后接粘连用户正文） */
function findKbMentionRangesInDisplay(
  displayQuery: string,
  kbList: IKnowledgeBaseItem[],
  kbDocUnit: string,
): IInlineMentionRange[] {
  const labels: string[] = [];
  kbList.forEach((kb) => {
    const docs = kb.docs || [];
    docs.forEach((doc) => {
      mentionLabelMatchVariants(
        withKnowledgeMentionTrigger(`${kb.kbName}-${doc.docName}`),
      ).forEach((label) => labels.push(label));
    });
    if (docs.length === 2) {
      mentionLabelMatchVariants(
        withKnowledgeMentionTrigger(
          `${kb.kbName}-${docs.map((doc) => doc.docName).join('、')}`,
        ),
      ).forEach((label) => labels.push(label));
    }
    mentionLabelMatchVariants(withKnowledgeMentionTrigger(kb.kbName))
      .forEach((label) => labels.push(label));

    const countPattern = new RegExp(
      `@${escapeRegExp(kb.kbName)} (\\d+)${escapeRegExp(kbDocUnit)}`,
      'g',
    );
    let countMatch: RegExpExecArray | null;
    while ((countMatch = countPattern.exec(displayQuery)) !== null) {
      mentionLabelMatchVariants(countMatch[0])
        .forEach((label) => labels.push(label));
    }
  });
  labels.sort((a, b) => b.length - a.length);

  const occupied = new Array(displayQuery.length).fill(false);
  const ranges: IInlineMentionRange[] = [];

  labels.forEach((label) => {
    let from = 0;
    while (from <= displayQuery.length - label.length) {
      const idx = displayQuery.indexOf(label, from);
      if (idx < 0) break;
      const end = idx + label.length;
      let taken = false;
      for (let i = idx;i < end;i += 1) {
        if (occupied[i]) {
          taken = true;
          break;
        }
      }
      if (!taken) {
        for (let i = idx;i < end;i += 1) { occupied[i] = true }
        ranges.push({
          key: `kb:${idx}:${label}`,
          kind: 'knowledge',
          start: idx,
          end,
          label,
          ids: [],
        });
        break;
      }
      from = idx + 1;
    }
  });

  return ranges.sort((a, b) => a.start - b.start);
}

function bodyNeedsKbDocs(
  body: string,
  kb: IKnowledgeBaseItem,
  kbDocUnit: string,
  kbExpandToken?: IKbExpandToken | null,
): boolean {
  const trimmedBody = body.trim();
  if (trimmedBody === kb.kbName) {
    return false;
  }
  const countRe = new RegExp(
    `^${escapeRegExp(kb.kbName)}\\s+\\d+${escapeRegExp(kbDocUnit)}$`,
  );
  if (countRe.test(trimmedBody)) {
    return true;
  }
  if (trimmedBody.startsWith(`${kb.kbName}-`)) {
    return true;
  }
  if (kbExpandToken) {
    const expandedNames = resolveDocNamesFromExpandToken(
      trimmedBody,
      kb,
      kbExpandToken,
    );
    if (expandedNames?.length) {
      return true;
    }
  }
  return false;
}

/**
 * 编辑还原：仅文档级 mention 需要拉 docs；整库 @库名 不在此列。
 * @param displayQuery - 编辑展示串
 * @param kbList - 知识库列表
 * @param kbDocUnit - 国际化「篇」等单位
 * @param kbExpandToken - 可选展开 token
 * @returns 需拉文档的 kbId（去重保序）
 */
export function resolveKbIdsNeedingDocsForHistory(
  displayQuery: string,
  kbList: IKnowledgeBaseItem[],
  kbDocUnit: string,
  kbExpandToken?: IKbExpandToken | null,
): string[] {
  if (!displayQuery?.trim() || !kbList.length) {
    return [];
  }
  const ranges = findKbMentionRangesInDisplay(displayQuery, kbList, kbDocUnit);
  const ids: string[] = [];
  const seen = new Set<string>();
  ranges.forEach((range) => {
    const body = stripKbTrigger(range.label);
    const kb = findKbForBody(kbList, body);
    if (!kb || !bodyNeedsKbDocs(body, kb, kbDocUnit, kbExpandToken)) {
      return;
    }
    const id = String(kb.kbId);
    if (seen.has(id)) {
      return;
    }
    seen.add(id);
    ids.push(id);
  });
  return ids;
}

/**
 * 从 display_query + kb_expand_token 还原知识库选区
 */
export function resolveKnowledgeFromDisplayQuery(
  displayQuery: string,
  kbList: IKnowledgeBaseItem[],
  kbDocUnit: string,
  kbExpandToken?: IKbExpandToken | null,
): ISelectedKnowledgeTagData[] {
  if (!displayQuery?.trim() || !kbList.length) return [];

  const ranges = findKbMentionRangesInDisplay(displayQuery, kbList, kbDocUnit);
  if (!ranges.length) return [];

  const result: ISelectedKnowledgeTagData[] = [];
  const seenIds = new Set<string>();

  ranges.forEach((range) => {
    const body = stripKbTrigger(range.label);
    const kb = findKbForBody(kbList, body);
    if (!kb) return;

    resolveDocsFromBody(body, kb, kbDocUnit, kbExpandToken).forEach((item) => {
      const key = getKnowledgeTagKey(item);
      if (seenIds.has(key)) return;
      seenIds.add(key);
      result.push(item);
    });
  });

  return result;
}

/**
 * 从展示串按库名找出已引用的 kbId，供编辑态只拉这些库的文档。
 * 不依赖 docs：`@库名` / `@库名-文档` / `@库名 N篇` 都能命中。
 * @param displayQuery - 编辑态展示串
 * @param kbList - 知识库列表
 * @returns 去重保序的 kbId
 */
export function resolveMentionedKbIdsFromDisplayQuery(
  displayQuery: string,
  kbList: IKnowledgeBaseItem[],
): string[] {
  if (!displayQuery?.trim() || !kbList.length) {
    return [];
  }
  const sorted = [...kbList].sort(
    (a, b) => b.kbName.length - a.kbName.length,
  );
  const ids: string[] = [];
  const seen = new Set<string>();
  sorted.forEach((kb) => {
    const name = (kb.kbName || '').trim();
    if (!name) {
      return;
    }
    const hit = mentionLabelMatchVariants(withKnowledgeMentionTrigger(name))
      .some((label) => displayQuery.includes(label));
    if (!hit) {
      return;
    }
    const id = String(kb.kbId);
    if (seen.has(id)) {
      return;
    }
    seen.add(id);
    ids.push(id);
  });
  return ids;
}
